import type { NoteGroup } from '../../types';
import { GROUP_COLORS } from './groupColors';

export type GroupFilter = 'all' | 'ungrouped' | string;

interface GroupListProps {
  groups: NoteGroup[];
  counts: Record<string, number>;
  ungroupedCount: number;
  activeFilter: GroupFilter;
  onSelect: (filter: GroupFilter) => void;
  onAddGroup: () => void;
  onEditGroup: (group: NoteGroup) => void;
  onDeleteGroup: (group: NoteGroup) => void;
}

export default function GroupList({
  groups,
  counts,
  ungroupedCount,
  activeFilter,
  onSelect,
  onAddGroup,
  onEditGroup,
  onDeleteGroup,
}: GroupListProps) {
  const totalCount =
    ungroupedCount + groups.reduce((sum, g) => sum + (counts[g.id] ?? 0), 0);

  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-background-200 bg-background-100">
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
          Nhóm ghi chú
        </p>

        <div className="space-y-0.5">
          <button
            type="button"
            onClick={() => onSelect('all')}
            className={`flex w-full items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
              activeFilter === 'all'
                ? 'bg-primary-100 text-primary-800'
                : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-inbox-line text-base" />
            </span>
            <span className="flex-1 text-left">Tất cả ghi chú</span>
            <span className="text-xs text-foreground-400">{totalCount}</span>
          </button>

          <button
            type="button"
            onClick={() => onSelect('ungrouped')}
            className={`flex w-full items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
              activeFilter === 'ungrouped'
                ? 'bg-primary-100 text-primary-800'
                : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-folder-line text-base text-foreground-400" />
            </span>
            <span className="flex-1 text-left">Chưa phân nhóm</span>
            <span className="text-xs text-foreground-400">
              {ungroupedCount}
            </span>
          </button>
        </div>

        {groups.length > 0 && (
          <div className="mt-3 space-y-0.5 border-t border-background-200 pt-3">
            {groups.map((group) => {
              const style = GROUP_COLORS[group.color] ?? GROUP_COLORS.primary;
              const isActive = activeFilter === group.id;
              return (
                <div
                  key={group.id}
                  className={`group flex items-center rounded-lg transition ${
                    isActive ? 'bg-background-200/80' : 'hover:bg-background-200'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(group.id)}
                    className={`flex min-w-0 flex-1 items-center gap-2.5 whitespace-nowrap px-3 py-2 text-sm font-medium transition ${
                      isActive ? 'text-foreground-900' : 'text-foreground-600'
                    }`}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${style.dot}`}
                      />
                    </span>
                    <span className="flex-1 truncate text-left">
                      {group.name}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {counts[group.id] ?? 0}
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center pr-1 opacity-0 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => onEditGroup(group)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-100 hover:text-foreground-700"
                      aria-label="Sửa nhóm"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteGroup(group)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700"
                      aria-label="Xóa nhóm"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-background-200 p-3">
        <button
          type="button"
          onClick={onAddGroup}
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border-2 border-dashed border-orange-400 px-3 py-2 text-sm font-medium text-orange-600 transition hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700"
        >
          <i className="ri-add-line text-base" />
          Thêm nhóm
        </button>
      </div>
    </aside>
  );
}